import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

const KNOWN_BRANDS = {
  mizuho: 'mizuho',
  drax: 'drax',
  // marex have diffent brand names (i.e.,Cognito group names) in uat and prod
  marex_wl: 'marexsolutions',
  marexsolutions: 'marexsolutions'
};
const PATH = '/assets/brands';

export type Brand = {
  brandName: string;
  mainLogo: string;
  loginLogo: string;
  adminLogo: string;
  poweredByLogo: string | false;
  disclaimer: boolean | string;
  supportContact: string;
};

const defaultBrand: Brand = {
  brandName: 'Digital Vega',
  mainLogo: `${PATH}/default/digitalvega.png`,
  loginLogo: `${PATH}/default/digitalvega300x125.png`,
  adminLogo: `${PATH}/default/digitalvega-admin-300x125.png`,
  poweredByLogo: `${PATH}/default/powered-by-digital-vega.png`,
  disclaimer: true,
  supportContact: 'support@digitalvega.com'
};

const storage = localStorage;

@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  public brandName = signal<string>(defaultBrand.brandName);
  public loginLogo = signal<string>(defaultBrand.loginLogo);
  public adminLogo = signal<string>(defaultBrand.adminLogo);
  public mainLogo = signal<string>(defaultBrand.mainLogo);
  public poweredByLogo = signal<string | false>(defaultBrand.poweredByLogo);
  public disclaimer = signal<boolean | string>(defaultBrand.disclaimer);
  public supportContact = signal<string>(defaultBrand.supportContact);
  public brandingLoaded = signal<boolean>(false);

  private http = inject(HttpClient);
  private title = inject(Title);

  constructor() {
    this.title.setTitle(`${defaultBrand.brandName} Exotics`);
    const brand = storage.getItem('brand');
    this.setBrand(brand);
  }

  public async setBrand(brand: string) {
    const hasBrand = !!brand;
    brand = KNOWN_BRANDS[brand] || 'default';
    await this.loadBrand(brand);
    if (hasBrand) {
      this.brandingLoaded.set(true);
    }
    setTimeout(() => {
      // shouldn't be needed, but just in case
      this.brandingLoaded.set(true);
    }, 2000);
  }

  private async loadBrand(brand: string): Promise<void> {
    storage.setItem('brand', brand);
    let brandData: Brand = await firstValueFrom(
      this.http.get<Brand>(`${PATH}/${brand}/brand.json`)
    );
    console.log('Brand set to', brand);

    let disclaimer = '';
    if (brandData.disclaimer) {
      disclaimer = await (
        await fetch(`${PATH}/${brand}/disclaimer.html`)
      ).text();
    }

    brandData = {
      ...brandData,
      mainLogo: this.brandItem(brandData.mainLogo, brand),
      adminLogo: this.brandItem(brandData.adminLogo, brand),
      loginLogo: this.brandItem(brandData.loginLogo, brand),
      poweredByLogo: this.brandItem(brandData.poweredByLogo, brand),
      disclaimer,
      supportContact: brandData.supportContact
    };
    this.setBrandData(brandData);
  }

  private brandItem(item: string | false, brand: string): string {
    return item ? `${PATH}/${brand}/${item}` : null;
  }

  private setBrandData(brandData: Brand) {
    this.brandingLoaded.set(true);
    this.brandName.set(brandData.brandName);
    this.mainLogo.set(brandData.mainLogo || defaultBrand.mainLogo);
    this.adminLogo.set(
      brandData.adminLogo || brandData.mainLogo || defaultBrand.adminLogo
    );
    this.loginLogo.set(brandData.loginLogo || defaultBrand.loginLogo);
    this.poweredByLogo.set(brandData.poweredByLogo);
    this.disclaimer.set(brandData.disclaimer || '');
    this.supportContact.set(
      brandData.supportContact || defaultBrand.supportContact
    );
    this.title.setTitle(`${brandData.brandName} Exotics`);
  }
}
